import { useEffect, useRef, useState } from 'preact/hooks';
import { api } from '../api.js';
import {
  categories,
  flow,
  lastCaptureCategoryId,
  navigate,
  closeQuickAdd,
  showToast,
  friendlyError,
  bumpTasksVersion,
} from '../store.js';
import { __ } from '../i18n.js';
import { PRIORITY_LEVELS, TIME_PRESETS, categoryIconName } from '../lib/labels.js';
import Modal from './Modal.jsx';
import Icon from './Icon.jsx';

/**
 * WP-6: the default category for a fresh sheet. flow.value.categoryId is
 * the current pick-a-task flow's category (WP-3: 0 means "all
 * categories", falsy like null/undefined) — a real specific category
 * there is a stronger signal of current intent than the merely-last-used
 * one, so it wins. Falls back to the first category if neither is set.
 * Re-evaluated fresh on every mount (the sheet fully unmounts on close —
 * see app.jsx — so this never reads a stale value from a previous open).
 */
function defaultCategoryId() {
  const contextId = flow.value.categoryId;
  if (contextId) return contextId;
  if (lastCaptureCategoryId.value) return lastCaptureCategoryId.value;
  return categories.value[0]?.id ?? null;
}

export default function QuickAddSheet() {
  const [title, setTitle] = useState('');
  const [priority, setPriority] = useState(null); // required, no preselection — save stays disabled until tapped
  const [effortMinutes, setEffortMinutes] = useState(30); // optional, same silent default create_task() already applies
  const [categoryId, setCategoryId] = useState(defaultCategoryId);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const titleRef = useRef(null);

  useEffect(() => {
    titleRef.current?.focus();
  }, []);

  const selectedCategory = categories.value.find((c) => c.id === categoryId) || null;
  const canSave = title.trim().length > 0 && priority !== null && categoryId !== null && !saving;

  async function save(andClear) {
    if (!canSave) return;
    setSaving(true);
    try {
      // Silent defaults for everything else (impact Normaal, no deadline,
      // no reward) — deliberately not restated here: ETM_Tasks::create_task()
      // already applies exactly these when the fields are omitted.
      // effort_minutes IS restated: it's a visible, adjustable field here.
      await api.tasks.create({
        title: title.trim(),
        category_id: Number(categoryId),
        priority: Number(priority),
        effort_minutes: Number(effortMinutes) || 30,
      });
      lastCaptureCategoryId.value = categoryId;
      bumpTasksVersion(); // keep the desktop Aankomend panel in sync
      showToast(__('Taak toegevoegd.', 'etm'), 'success');
      if (andClear) {
        // Batch brain-dumps: clear title AND priority (required means
        // required — no carrying a previous conscious choice forward),
        // but keep the category, since a run of quick-adds is usually
        // all the same category.
        setTitle('');
        setPriority(null);
        titleRef.current?.focus();
      } else {
        closeQuickAdd();
      }
    } catch (err) {
      showToast(friendlyError(err), 'error');
    } finally {
      setSaving(false);
    }
  }

  function openMoreDetails() {
    // Hand off to the full form, carrying over exactly what's been
    // entered so far (brief: "carries values over losslessly"). No task
    // id — TaskFormScreen treats this as a create-mode prefill, same as
    // its own blank "+" entry point.
    closeQuickAdd();
    navigate('taskForm', {
      task: {
        title: title.trim(),
        category_id: categoryId,
        priority: priority ?? 2,
        effort_minutes: effortMinutes,
      },
    });
  }

  function goCreateCategory() {
    closeQuickAdd();
    navigate('category', { manage: true });
  }

  // WP-7 (brief 7d): the self-heal in 7a should make this unreachable in
  // practice, but a disabled save button with no visible reason must never
  // recur — if it somehow does happen, say so and offer the fix directly
  // instead of a silently-stuck sheet.
  if (categories.value.length === 0) {
    return (
      <Modal title={__('Snel toevoegen', 'etm')} onClose={closeQuickAdd}>
        <div class="etm-empty-state">{__('Je hebt nog geen categorieën.', 'etm')}</div>
        <button type="button" class="etm-btn etm-btn-primary" onClick={goCreateCategory}>
          {__('Categorie aanmaken', 'etm')}
        </button>
      </Modal>
    );
  }

  return (
    <Modal title={__('Snel toevoegen', 'etm')} onClose={closeQuickAdd}>
      <div class="etm-form-group">
        <input
          ref={titleRef}
          type="text"
          class="etm-form-input"
          placeholder={__('Wat moet er gebeuren?', 'etm')}
          value={title}
          onInput={(e) => setTitle(e.currentTarget.value)}
          maxLength={200}
        />
      </div>

      <div class="etm-form-group">
        <span class="etm-form-label">{__('Prioriteit', 'etm')}</span>
        <div class="etm-priority-selector">
          {PRIORITY_LEVELS.map((level) => (
            <button
              key={level.value}
              type="button"
              class={`etm-priority-btn${priority === level.value ? ' active' : ''}`}
              onClick={() => setPriority(level.value)}
            >
              {level.label()}
            </button>
          ))}
        </div>
      </div>

      <div class="etm-form-group">
        <span class="etm-form-label">{__('Geschatte tijd', 'etm')}</span>
        <div class="etm-priority-selector">
          {TIME_PRESETS.map((preset) => (
            <button
              key={preset.minutes}
              type="button"
              class={`etm-priority-btn${Number(effortMinutes) === preset.minutes ? ' active' : ''}`}
              onClick={() => setEffortMinutes(preset.minutes)}
            >
              {preset.sublabel()}
            </button>
          ))}
        </div>
        <input
          id="etm-quickadd-effort"
          class="etm-form-input etm-effort-free-input"
          type="number"
          min="1"
          value={effortMinutes}
          onInput={(e) => setEffortMinutes(e.currentTarget.value)}
          aria-label={__('Of vul zelf minuten in', 'etm')}
          placeholder={__('Of vul zelf minuten in', 'etm')}
        />
      </div>

      <div class="etm-form-group">
        <button
          type="button"
          class="etm-category-chip"
          aria-expanded={pickerOpen}
          onClick={() => setPickerOpen((open) => !open)}
        >
          <Icon name={categoryIconName(selectedCategory?.icon)} size={18} />
          {selectedCategory?.name || __('Kies categorie', 'etm')}
          <Icon name="keyboard_arrow_down" size={16} />
        </button>

        {pickerOpen ? (
          <div class="etm-quick-add-picker">
            {categories.value.map((cat) => (
              <button
                key={cat.id}
                type="button"
                class="etm-quick-add-picker__item"
                onClick={() => {
                  setCategoryId(cat.id);
                  setPickerOpen(false);
                }}
              >
                <Icon name={categoryIconName(cat.icon)} size={18} /> {cat.name}
              </button>
            ))}
          </div>
        ) : null}
      </div>

      <button type="button" class="etm-btn etm-btn-ghost" onClick={openMoreDetails}>
        {__('Meer details', 'etm')}
      </button>

      <button type="button" class="etm-btn etm-btn-primary" disabled={!canSave} onClick={() => save(false)}>
        {saving ? __('Bezig…', 'etm') : __('Opslaan', 'etm')}
      </button>
      <button type="button" class="etm-btn etm-btn-secondary" disabled={!canSave} onClick={() => save(true)}>
        {__('Opslaan + nog een', 'etm')}
      </button>
    </Modal>
  );
}
